import * as React from "react";
import { FvcAnalysisSet, FvcAnalysisSetTest } from "webportalclient";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { OverreadStatusEnum, OverreadStatusIdEnum } from "../../misc/api/apiTypes";
import { NextGenFixedCommentsState, NextGenTestFixedCommentState } from "../Spirometry/Testing/UseNextGenFixedComments";
import { AcceptabilityValidationWizard, AcceptabilityValidationWizardState } from "./AcceptabilityValidationWizard";
import { AcceptabilityNextGenState } from "./UseAcceptabilityNextGen";
import { useFixedCommentsState } from '../../Core/FixedCommentsContext';
import { FixedCommentSetTypeEnum } from "../../misc/api/FixedComments/FixedCommentSetTypes";

interface OverreadingAcceptabilityDashboardProps {
    analysisSetId: string;
    isViewOnly: boolean;
    fixedCommentsDispatch;
    fixedCommentState: NextGenFixedCommentsState;
    acceptabilityState: AcceptabilityNextGenState;
    sessionType: SessionTypes;
    overreadStatus: string;
    onAcceptabilitySelected: (auditAnnotation: string, decision: OverreadStatusEnum) => void;
    onNoSessionCommentsSelected?: () => void;
    onCancel: () => void;
}


export const OverreadingAcceptabilityDashboard = (props: OverreadingAcceptabilityDashboardProps) => {
    const isVisible = !props.isViewOnly && props.fixedCommentState.areFixedCommentsLoaded;
    const [selectedDecision, setSelectedDecision] = React.useState<OverreadStatusEnum>(null);
    const [isAuditModalVisible, setIsAuditModalVisible] = React.useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [validationWizardStep, setValidationWizardStep] = React.useState<AcceptabilityValidationWizardState>(AcceptabilityValidationWizardState.SessionFixedComments);
    const [rejectedTestsMissingComments, setRejectedTestsMissingComments] = React.useState<number[]>([]);
    const { sessionDefaultComments } = useFixedCommentsState();
    const sessionDefaultComment = sessionDefaultComments.get(`${props.sessionType}-${FixedCommentSetTypeEnum.Overread}`);

    const selectedSessionComments = props.fixedCommentState.sessionFixedComments.comments.filter(x => x.selected).length;

    const handleRejectedTestValidation = (testResults: FvcAnalysisSetTest[], testFixedComments: NextGenTestFixedCommentState[]) => {
        const rejectedTestsMissingComments = [];
        for (let i = 0; i < testResults.length; i++) {
            if (testResults[i].accepted) continue;

            const currentTestComments = testFixedComments.filter(tfc => tfc.testId === testResults[i].testId);

            if (currentTestComments.filter(tc => tc.comments.some(c => c.selected)).length === 0) {
                rejectedTestsMissingComments.push(testResults[i].test.index);
            }
        }

        return rejectedTestsMissingComments
    }

    const handleAcceptRejectClick = (selectedAcceptability: OverreadStatusEnum) => {
        setSelectedDecision(selectedAcceptability);

        if (selectedAcceptability !== OverreadStatusEnum.Pending) {
            const rejectedTests = handleRejectedTestValidation(props.acceptabilityState.analysisSet.tests, props.fixedCommentState.testFixedComments);
            setRejectedTestsMissingComments(rejectedTests);

            if (rejectedTests.length > 0) {
                setIsAuditModalVisible(true);
                return setValidationWizardStep(AcceptabilityValidationWizardState.TestFixedComments);
            }

            if (selectedSessionComments === 0
                && (selectedAcceptability === OverreadStatusEnum.Rejected
                    || (selectedAcceptability === OverreadStatusEnum.Accepted && (sessionDefaultComment === null || sessionDefaultComment === undefined))
                )) {
                setIsAuditModalVisible(true);
                return setValidationWizardStep(AcceptabilityValidationWizardState.SessionFixedComments);
            }
        }
        setIsAuditModalVisible(true);
        setValidationWizardStep(AcceptabilityValidationWizardState.Audit);
    }

    const handleAcceptabilitySubmit = (annotation: string, selectedDecision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        props.onAcceptabilitySelected(annotation, selectedDecision);
        setIsSubmitting(false);
        setIsAuditModalVisible(false);
    }

    if (isVisible) {
        return (
            <>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <button id="SuccessButton" data-automationid="AcceptabilityDashboardSuccessButton" className="btn btn-success" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Accepted)}>
                            <i className="fa fa-check" aria-hidden="true"></i>  Accept
                        </button>&nbsp;
                        <button id="PendingButton" data-automationid="AcceptabilityDashboardPendingButton" className="btn btn-warning" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Pending)}>
                            <i className="fa fa-circle-o" aria-hidden="true"></i> Pending
                        </button>&nbsp;
                        <button id="RejectButton" data-automationid="AcceptabilityDashboardRejectButton" className="btn btn-danger" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Rejected)}>
                            <i className="fa fa-times" aria-hidden="true"></i> Reject
                        </button>
                    </div>
                </div>
                {isAuditModalVisible ?
                    <AcceptabilityValidationWizard
                        visibleStep={validationWizardStep}
                        sessionFixedComments={props.fixedCommentState.sessionFixedComments}
                        dispatch={props.fixedCommentsDispatch}
                        isSubmitting={isSubmitting}
                        rejectedTestsMissingComments={rejectedTestsMissingComments}
                        overreadStatus={props.overreadStatus}
                        onCancel={() => setIsAuditModalVisible(false)}
                        onFixedCommentSelected={() => setValidationWizardStep(AcceptabilityValidationWizardState.Audit)}
                        onSubmit={(annotation) => handleAcceptabilitySubmit(annotation, selectedDecision)}
                    />
                    : null}
            </>
        );
    } else {
        return <button id="CancelButton" className="btn btn-primary" onClick={() => history.back()}>Cancel</button>;
    }
}
